import {
  DELETE_ALL_DEPLOYMENTS,
  UPDATE_DEPLOYMENT,
  SET_DEPLOYMENTS,
  SET_DEPLOYMENT_MODBUS,
  SET_DEPLOYMENT_DISCOVERY,
  SET_HEARTBEATS,
  SET_DEPLOYMENT_CONFIG,
  CLEAR_STAGING_LOAD_TIMES,
} from './actiontypes';
import {
  getDeployments,
  getDeploymentModbus,
  patchDeployment,
  upsertDeployment,
  getDeploymentConfig,
  upsertDeploymentConfig,
  createDeploymentCollector,
  updateDeploymentCollector,
  upsertModbusDeployment,
  deleteModbusDeployment,
  getDiscovery,
  postDiscovery,
  getSiteHeartbeats,
  associateBuildings,
} from '../../api/Api';
import { sendData } from './util';
import { unique } from '../../utils/collections';

export function deleteAllDeploymentsAction() {
  return {
    type: DELETE_ALL_DEPLOYMENTS,
  };
}

export const getDeploymentsAction =
  ({ force = false }) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
      entities: {
        deployments: { deployments },
      },
    } = getState();

    if (!force && Object.keys(deployments).length > 0) return Promise.resolve();
    return getDeployments(jwt).then((data) => dispatch(sendData(SET_DEPLOYMENTS, data)));
  };

export const getDeploymentModbusAction = (site) => (dispatch, getState) => {
  const {
    user: { jwt },
    entities: {
      staging: { devicesLoadTime, pointsLoadTime, equipmentLoadTime },
    },
  } = getState();
  return getDeploymentModbus(jwt, site).then((data) => {
    if (
      data.modbus_deployments.some(
        (d) =>
          d.last_status_update >
          Math.min(
            devicesLoadTime[d.building_id],
            equipmentLoadTime[d.building_id],
            pointsLoadTime[d.building_id]
          )
      )
    )
      unique(data.modbus_deployments.map((d) => d.building_id)).forEach((buildingId) =>
        dispatch({ type: CLEAR_STAGING_LOAD_TIMES, buildingId })
      );
    return dispatch({ type: SET_DEPLOYMENT_MODBUS, site, data: data.modbus_deployments });
  });
};

export const getDeploymentDiscoveryAction = (site) => (dispatch, getState) => {
  const {
    user: { jwt },
    entities: {
      staging: { devicesLoadTime, pointsLoadTime, equipmentLoadTime },
    },
  } = getState();
  return getDiscovery(jwt, site).then((data) => {
    if (
      data.bacnet_discovery.some(
        (d) =>
          d.last_status_update >
          Math.min(
            devicesLoadTime[d.building_id],
            equipmentLoadTime[d.building_id],
            pointsLoadTime[d.building_id]
          )
      )
    )
      unique(data.bacnet_discovery.map((d) => d.building_id)).forEach((buildingId) =>
        dispatch({ type: CLEAR_STAGING_LOAD_TIMES, buildingId })
      );
    return dispatch({ type: SET_DEPLOYMENT_DISCOVERY, site, data: data.bacnet_discovery });
  });
};

export const startDeploymentDiscoveryAction =
  (site, buildingId, port, min, max) => (dispatch, getState) => {
    const {
      user: { jwt },
    } = getState();
    return postDiscovery(jwt, site, buildingId, port, min, max).then(() =>
      dispatch(getDeploymentDiscoveryAction(site))
    );
  };

export const getHeartbeatsAction =
  (site, force = false) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
      entities: {
        deployments: { heartbeats },
      },
    } = getState();
    const siteBeats = heartbeats[site];
    // If we have already loaded into redux, don't reload
    if (!force && siteBeats) return Promise.resolve();
    return getSiteHeartbeats(jwt, site, '72h').then((hbs) =>
      dispatch(sendData(SET_HEARTBEATS, { site, heartbeats: hbs }))
    );
  };

export const getDeploymentConfigAction =
  (site, force = false) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
      entities: {
        deployments: { configs },
      },
    } = getState();
    const siteConfigs = configs[site];
    // If we have already loaded into redux, don't reload
    if (!force && siteConfigs) return Promise.resolve();
    return getDeploymentConfig(jwt, site).then((data) =>
      dispatch(sendData(SET_DEPLOYMENT_CONFIG, data))
    );
  };

const DEPLOYMENT_READ_ONLY_FIELDS = ['device_ips', 'buildings', 'last_heartbeat', 'id'];

export const upsertDeploymentAction = (deployment) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  const payload = { ...deployment };
  DEPLOYMENT_READ_ONLY_FIELDS.forEach((f) => {
    delete payload[f];
  });
  // TODO: Replace with direct item update
  return upsertDeployment(jwt, payload).then(() => dispatch(getDeploymentsAction({ force: true })));
};

export const patchDeploymentAction = (site, newName) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  const payload = { name: newName };
  return patchDeployment(jwt, site, payload).then((response) =>
    dispatch(sendData(UPDATE_DEPLOYMENT, response))
  );
};

const COLLECTOR_READ_ONLY_FIELDS = [
  'site',
  'private_ip',
  'api_key',
  'device_ips',
  'wg_pubkey',
  'last_heartbeat',
];

export const updateDeploymentCollectorAction = (collector) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  const payload = { ...collector };
  COLLECTOR_READ_ONLY_FIELDS.forEach((f) => {
    delete payload[f];
  });
  return updateDeploymentCollector(jwt, collector.site, collector.id, payload).then(() =>
    dispatch(getDeploymentConfigAction(collector.site, true))
  );
};

export const createDeploymentActionCreator =
  (site, name, orgId, active = true) =>
  (dispatch, getState) => {
    const {
      user: { jwt },
    } = getState();
    return upsertDeployment(jwt, { site, name, org_id: orgId, active }).then((result) => {
      dispatch(getDeploymentsAction({ force: true }));
      return result;
    });
  };

export const createDeploymentCollectorAction = (site, config) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return createDeploymentCollector(jwt, site, config).then((result) => {
    dispatch(getDeploymentConfigAction(site, true));
    return result;
  });
};

export const upsertDeploymentConfigAction = (site, config) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return upsertDeploymentConfig(jwt, site, config).then(() =>
    dispatch(getDeploymentConfigAction(site, true))
  );
};

export const upsertModbusDeploymentAction = (site, modbusConfig) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return upsertModbusDeployment(jwt, site, modbusConfig).then(() =>
    dispatch(getDeploymentModbusAction(site))
  );
};

export const deleteModbusDeploymentAction = (site, modbusConfig) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return deleteModbusDeployment(
    jwt,
    site,
    modbusConfig.ip,
    modbusConfig.port,
    modbusConfig.unit
  ).then(() => dispatch(getDeploymentModbusAction(site)));
};

export const associateBuildingsAction = (site, buildings) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return associateBuildings(jwt, site, buildings).then(() =>
    dispatch(getDeploymentsAction({ force: true }))
  );
};
